import {
  type FormEvent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog.js";
import { Input } from "../ui/input.js";

// =============================================================================
// PromptDialog + usePrompt() — promise-based replacement for window.prompt.
//
// Mirrors apps/main's existing ConfirmDialog pattern (V2-QUALITY-PLAN §3.5 +
// P2.2, 2026-04-24). Provides an imperative `prompt(options)` that returns
// Promise<string | null>, backed by shadcn AlertDialog.
//
// Wire <PromptProvider> once at the app root (main.tsx) alongside
// ConfirmProvider:
//
//   const prompt = usePrompt();
//   const reason = await prompt({
//     title: 'Reason for revoking this role?',
//     placeholder: 'e.g. policy violation',
//     validate: (v) => v.trim().length > 0 ? null : 'Required',
//   });
//   if (reason === null) return; // user cancelled
// =============================================================================

export interface PromptOptions {
  title: string;
  description?: ReactNode;
  /** Placeholder for the input. */
  placeholder?: string;
  /** Default value to pre-fill. */
  defaultValue?: string;
  /** Submit label. Default "Submit". */
  submitLabel?: string;
  /** Cancel label. Default "Cancel". */
  cancelLabel?: string;
  /** Inline validator — return non-null string to reject the input. */
  validate?: (value: string) => string | null;
  /** If true, empty string is valid (user can submit "no reason"). Default false. */
  allowEmpty?: boolean;
}

interface PromptApi {
  prompt(options: PromptOptions): Promise<string | null>;
}

const PromptContext = createContext<PromptApi | null>(null);

interface PendingState extends PromptOptions {
  resolve: (value: string | null) => void;
}

export function PromptProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const api = useMemo<PromptApi>(
    () => ({
      prompt: (options) =>
        new Promise<string | null>((resolve) => {
          setValue(options.defaultValue ?? '');
          setError(null);
          setPending({ ...options, resolve });
        }),
    }),
    [],
  );

  const close = useCallback(
    (result: string | null) => {
      if (!pending) return;
      pending.resolve(result);
      setPending(null);
      setValue('');
      setError(null);
    },
    [pending],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) close(null);
    },
    [close],
  );

  // ENG-478/572 — this used to run off `<form onSubmit>` fired by an
  // AlertDialogAction with type="submit". Radix's AlertDialogAction closes
  // the dialog (unmounting this <form>) in the same click as the submit
  // button, which races the browser's native form-submission dispatch: the
  // form is gone from the DOM before the 'submit' event can fire, so Chrome/
  // Firefox cancel it with "Form submission canceled because the form is not
  // connected" and onSubmit NEVER RUNS — zero requests are ever sent (this is
  // exactly what broke the Void invoice reason dialog). Fixed by driving
  // submission from a plain onClick (submitFromButton) that needs no
  // FormEvent, instead of depending on the native submit dispatch surviving
  // the dialog's own close. The <form>'s onSubmit is kept ONLY for Enter-key-
  // in-the-input, which does not go through AlertDialogAction and is not
  // subject to the same race.
  const submit = useCallback(() => {
    if (!pending) return;
    const trimmed = value;
    if (!pending.allowEmpty && trimmed.trim().length === 0) {
      setError('Required');
      return;
    }
    if (pending.validate) {
      const msg = pending.validate(trimmed);
      if (msg) {
        setError(msg);
        return;
      }
    }
    close(trimmed);
  }, [close, pending, value]);

  const handleFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      submit();
    },
    [submit],
  );

  return (
    <PromptContext.Provider value={api}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={onOpenChange}>
        {pending && (
          <AlertDialogContent>
            <form onSubmit={handleFormSubmit}>
              <AlertDialogHeader>
                <AlertDialogTitle>{pending.title}</AlertDialogTitle>
                {pending.description && (
                  <AlertDialogDescription>{pending.description}</AlertDialogDescription>
                )}
              </AlertDialogHeader>
              <div className="my-3">
                <Input
                  autoFocus
                  type="text"
                  value={value}
                  placeholder={pending.placeholder}
                  onChange={(e) => {
                    setValue(e.target.value);
                    if (error) setError(null);
                  }}
                />
                {error ? (
                  <p role="alert" className="mt-1 text-xs text-destructive">
                    {error}
                  </p>
                ) : null}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel type="button" onClick={() => close(null)}>
                  {pending.cancelLabel ?? 'Cancel'}
                </AlertDialogCancel>
                {/* type="button", NOT "submit" — see the `submit` callback
                    comment above for why relying on native form submission
                    here silently drops every click. */}
                <AlertDialogAction type="button" onClick={submit}>
                  {pending.submitLabel ?? 'Submit'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </PromptContext.Provider>
  );
}

export function usePrompt(): (options: PromptOptions) => Promise<string | null> {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    throw new Error('usePrompt() must be used inside <PromptProvider>');
  }
  return ctx.prompt;
}
